import { Box, Collapse, Flex, Text, useMediaQuery } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import FullScreenLoader from 'components/FullScreenLoader';
import { useBloxBlockPageTitle } from 'hooks/useBloxDocumentTitle';
import { HEADER_HEIGHT } from 'projectConstants';
import {
  getExandSideInputStatus,
  handleExpandSideInput,
  resetBuilderBlockTableData,
} from 'redux/BuilderModeSlice';
import {
  FetchFormulaList,
  FetchPlannerModeBlock,
  FetchPlannerModeModel,
  FetchScenarios,
  getBaseScenario,
  getPlannerBlock,
  getPlannerModel,
  getPlannerMode,
  getSelectedIndicators,
  getToggleInputPanel,
  getToolbarType,
  handleAddIndicator,
  handlePlannerMode,
  handleToggleInputPanel,
  resetBlockTableData,
  toggleToolbarType,
} from 'redux/PlannerModeSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import { BuilderModeV1IndicatorIconProvider } from 'pages/BuilderModeV1/icons/BuilderModeV1IndicatorIconContext';
import { neutral, radius } from 'styles/revampTokens';

import BuilderModeTable from './Table';
import IndicatorPageOPZ from './IndicatorPageOPZ';
import BuilderModeNavHeader from './components/BuilderModeNavHeader';
import BuilderModeHeader from './components/BuilderModeHeader';
import ScenarioForecast from './ScenarioForecast';
import BlockSetting from './BlockSetting';
import CategoryTabsBar, { BuilderModeTab } from './components/CategoryTabsBar';
import { ModernIndicatorIconProvider } from './icons/ModernIndicatorIconContext';
import FigmaAddIndicatorPlusIcon from './icons/FigmaAddIndicatorPlusIcon';
import FormulaBar from './components/FormulaBar';

// Same page gradient Dashboard/Dimensions/Blocks/Plan revamps all use —
// inlined here (rather than importing it) so this branch stays
// self-contained and doesn't reach into another module's branch.
const BUILDER_MODE_REVAMP_GRADIENT =
  'linear-gradient(180deg, #FCFAF8 14.67%, #F5F2FF 48.8%, #FFF0FC 100%)';

const BuilderModeRevamp: React.FC = () => {
  const { modelId, blockId } = useParams();
  const modelIdFromParameters = useParams().modelId;
  const [loader, setLoader] = useState(true);
  const toggleInputPanel = useSelector(getToggleInputPanel);
  const dispatch: AppDispatch = useDispatch();
  const blockDetails = useSelector(getPlannerBlock);
  const plannerModel = useSelector(getPlannerModel);
  const isExpand = useSelector(getExandSideInputStatus);
  const isBuilderModeOn = useSelector(getPlannerMode);
  const toolbarType = useSelector(getToolbarType) as BuilderModeTab;
  const selectedIndicators = useSelector(getSelectedIndicators);
  const baseScenario = useSelector(getBaseScenario);

  const [isTablet] = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const initialData = async () => {
      setLoader(true);
      dispatch(resetBlockTableData());
      dispatch(resetBuilderBlockTableData());
      dispatch(FetchPlannerModeModel(Number(modelId)));
      dispatch(FetchFormulaList(Number(modelId)));
      dispatch(FetchPlannerModeBlock(Number(blockId))).then(async () => {
        if (modelIdFromParameters) await dispatch(FetchScenarios(modelIdFromParameters));
        setLoader(false);
      });
    };
    initialData();
  }, [blockId, modelId, modelIdFromParameters]);

  useBloxBlockPageTitle(loader ? undefined : blockDetails?.name, plannerModel?.name, 'Builder');

  useEffect(() => {
    return () => {
      dispatch(handleExpandSideInput(false));
    };
  }, []);

  if (loader) {
    return <FullScreenLoader height={`calc(100vh - ${HEADER_HEIGHT})`} />;
  }

  return (
    // This mounts inside `PlanPageDetailsOPZ`'s wrapper, which is already
    // height-capped to `calc(100vh - headerHeight)` with `overflow: hidden`.
    // `minH='100vh'` here was taller than that available space, so this Box
    // always got hard-clipped by the parent's overflow:hidden with no
    // scrollbar of its own — height/minH=0 instead lets it fill exactly
    // what the parent actually gives it, so the internal `overflowY='auto'`
    // area further down is the one that scrolls, not an invisible clip.
    <Box height='100%' minH={0} bg={BUILDER_MODE_REVAMP_GRADIENT}>
      <ModernIndicatorIconProvider value>
        <BuilderModeV1IndicatorIconProvider value={false}>
          {/* No gap — the input panel docks flush against the table area and
              the viewport edge; its own left border is the separator. */}
          <Flex width={'100%'} h='100%' flexDirection={isExpand ? 'row-reverse' : 'row'}>
            <Box
              width={toggleInputPanel ? (isExpand && toggleInputPanel ? '0%' : '68%') : '100%'}
              h={'100%'}
              display={isExpand ? 'none' : 'flex'}
              flexDirection='column'
            >
              <BuilderModeNavHeader />
              <BuilderModeHeader
                title={blockDetails?.name ?? ''}
                scenarioName={baseScenario?.name ?? 'Baseline'}
                selectedDimensionCount={
                  blockDetails?.dimensions?.filter((dimension: any) => dimension.name !== 'Time')
                    .length ?? 0
                }
                isBuilderModeOn={isBuilderModeOn}
                onBuilderModeToggle={(checked) => dispatch(handlePlannerMode(checked))}
                // The header's own pills were inert — it takes onScenarioClick
                // and onDimensionsClick, and nothing was passing them. These
                // two carry the working scenario and dimension logic.
                scenarioControl={<ScenarioForecast />}
                dimensionsControl={<BlockSetting />}
              />
              <CategoryTabsBar
                activeTab={toolbarType}
                onTabChange={(tab) => {
                  dispatch(handleAddIndicator(false));
                  dispatch(toggleToolbarType(tab));
                  if (toggleInputPanel) dispatch(handleToggleInputPanel());
                }}
                onAddIndicator={() => dispatch(handleAddIndicator(true))}
              />
              <Box
                flex='1'
                minH={0}
                px={isTablet ? 4 : '12px'}
                py='20px'
                display='flex'
                flexDirection='column'
                overflow='hidden'
              >
                {(toolbarType === 'Data' || toolbarType === 'Property') && (
                  <Collapse in={Boolean(selectedIndicators)} animateOpacity>
                    <FormulaBar />
                  </Collapse>
                )}
                {/* Card shell matching PlanRevamp's `PlanCard` (white surface,
                    one neutral.border hairline, radius.surface) — the table
                    previously sat edge-to-edge with no framing at all.
                    flex/minH bound the card to the viewport (this Box's
                    available height) — the table scrolls internally past
                    that point (see ModernDataTable's own grid container)
                    instead of growing the page. */}
                <Box
                  flex='1'
                  minH={0}
                  bg={neutral.surface}
                  border='1px solid'
                  borderColor={neutral.border}
                  borderRadius={radius.surface}
                  overflow='hidden'
                  display='flex'
                  flexDirection='column'
                >
                  <BuilderModeTable />
                </Box>
                {(toolbarType === 'Data' || toolbarType === 'Property') && (
                  <Flex
                    as='button'
                    type='button'
                    onClick={() => dispatch(handleAddIndicator(true))}
                    alignItems='center'
                    justifyContent='center'
                    gap='8px'
                    width='100%'
                    mt='12px'
                    py='8px'
                    px='12px'
                    bg='white'
                    border='1px solid #D1D6DD'
                    borderRadius={radius.control}
                    cursor='pointer'
                    flexShrink={0}
                    transition='background 0.15s ease, transform 0.1s ease'
                    _hover={{ bg: '#F5F0FF' }}
                    _active={{ bg: '#F5F0FF', transform: 'scale(0.97)' }}
                  >
                    <FigmaAddIndicatorPlusIcon width={13} height={13} color='#121826' />
                    <Text
                      fontSize='13px'
                      fontWeight={500}
                      fontFamily='Roboto Medium, Roboto Regular'
                      color='#121826'
                    >
                      Add Indicator
                    </Text>
                  </Flex>
                )}
              </Box>
            </Box>
            {toggleInputPanel && (
              <Box
                className={`input-panel${toggleInputPanel ? ' visible' : ' hidden'}`}
                width={toggleInputPanel ? (isExpand && toggleInputPanel ? '100%' : '32%') : '0'}
                // Docked, not floating: the panel runs the full height of the
                // work area and meets the edge squarely, so a single divider
                // separates it from the table. A rounded, shadowed, top-inset
                // card read as something hovering over the page.
                h='100%'
                minH={0}
                bg={neutral.surface}
                borderLeft='1px solid'
                borderColor={neutral.border}
              >
                <IndicatorPageOPZ />
              </Box>
            )}
          </Flex>
        </BuilderModeV1IndicatorIconProvider>
      </ModernIndicatorIconProvider>
    </Box>
  );
};

export default withLDConsumer()(BuilderModeRevamp);
